import React, { useCallback, useMemo } from "react";
import { usePictureSets } from "../../hooks/usePictureSets";
import List from "@mui/material/List";
import { ListItem, ListItemButton, ListItemIcon, Typography, } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import ListItemText from "@mui/material/ListItemText";
import { Trans, useTranslation } from "react-i18next";
import Checkbox from "@mui/material/Checkbox";
import Popup, { PopupActions, PopupContent, PopupHeading, } from "../../../components/Common/Popup";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import AddMoreButton from "../../../components/Common/AddMoreButton";
import { getYUPValidators } from "../../../components/yupvalidators";
import LoadingButton from "../../../components/LoadingButton";
import { callAPI } from "../../../lib/common";
import { SubjectYearListHeader } from "@/react/Webshop/Components/Checkout/SubjectSelector";
const useStyles = makeStyles((theme) => ({
    recipientList: {
        listStyle: "none",
        margin: 0,
        padding: 0,
        "& > li": {
            marginBottom: theme.spacing(2),
        },
    },
    popupContainer: {
        maxWidth: "400px",
        textAlign: "center",
    },
    subjectList: {
        maxHeight: "300px",
        overflowY: "auto",
        marginBottom: theme.spacing(4),
    },
}));
const SharePictureSets = ({ onClose: _onClose }) => {
    const pictureSetsContext = usePictureSets();
    const [selectedPictureSets, setSelectedPictureSets] = React.useState([]);
    const [state, _setState] = React.useState("select-picture-sets");
    const [recipients, setRecipients] = React.useState([""]);
    const [sentRecipients, setSentRecipients] = React.useState([""]);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const { t } = useTranslation();
    // Hack to work with FocusTrap closing dialog on unmount
    const isChangingStep = React.useRef(false);
    const setState = useCallback((state) => {
        isChangingStep.current = true;
        console.log("Setting is changing step", state);
        _setState(state);
    }, [state]);
    const onClose = useCallback(() => {
        console.log("On close", isChangingStep);
        if (isChangingStep.current) {
            isChangingStep.current = false;
            return;
        }
        _onClose();
    }, [_onClose, isChangingStep]);
    const handleAddRecipient = () => {
        setRecipients([...recipients, ""]);
    };
    const handleInviteMore = () => {
        setRecipients([""]);
        setSelectedPictureSets([]);
        setState("select-picture-sets");
    };
    const handleUpdateRecipientByIndex = (index) => (e) => {
        const newRecipients = [...recipients];
        newRecipients[index] = e.target.value;
        setRecipients(newRecipients);
    };
    const handleSelectSubjectsComplete = () => {
        setState("select-recipients");
    };
    const handleSelectSubjects = () => {
        setState("select-picture-sets");
    };
    const handleSelectRecipientsComplete = () => {
        setIsSubmitting(true);
        const validRecipients = getValidRecipients(recipients, t);
        let queryString = arrayToQueryString(validRecipients, "email");
        queryString += "&" + arrayToQueryString(selectedPictureSets, "subjects");
        callAPI("/sections/prophoto/ajax/do_tipafriend.php?" + queryString)
            .then((res) => {
            if (res.error) {
                alert(res.error);
                return;
            }
            setSentRecipients(res.recipients);
            setState("show-receipt");
        })
            .catch((err) => {
            alert(t("Error sharing picture sets, please try again"));
        })
            .finally(() => {
            setIsSubmitting(false);
        });
    };
    const handleToggle = (value) => {
        const currentIndex = selectedPictureSets.indexOf(value);
        const newChecked = [...selectedPictureSets];
        if (currentIndex === -1) {
            newChecked.push(value);
        }
        else {
            newChecked.splice(currentIndex, 1);
        }
        console.log("Handle toggle", newChecked);
        setSelectedPictureSets(newChecked);
    };
    if (!pictureSetsContext || !pictureSetsContext.pictureSets) {
        return (React.createElement("div", null,
            React.createElement(Trans, null, "Loading")));
    }
    const { pictureSets } = pictureSetsContext;
    if (pictureSets.length === 0) {
        return React.createElement(NoPictureSetsAvailable, { onClose: onClose });
    }
    if (state == "select-picture-sets") {
        return (React.createElement(SelectPictureSets, { pictureSets: pictureSets, onClose: onClose, selectedPictureSets: selectedPictureSets, handleToggle: handleToggle, onSelectSubjectsComplete: handleSelectSubjectsComplete }));
    }
    else if (state === "select-recipients") {
        return (React.createElement(SelectRecipients, { onClose: onClose, selectedPictureSets: selectedPictureSets, onSelectRecipientsComplete: handleSelectRecipientsComplete, recipients: recipients, onAddRecipient: handleAddRecipient, onUpdateRecipientByIndex: handleUpdateRecipientByIndex, onGoBack: handleSelectSubjects, isSubmitting: isSubmitting }));
    }
    else if (state === "show-receipt") {
        return (React.createElement(ShowReceipt, { onClose: onClose, recipients: sentRecipients, onInviteMore: handleInviteMore }));
    }
    {
        return React.createElement("div", null, "undefined");
    }
};
const ShowReceipt = ({ recipients, onClose, onInviteMore, }) => {
    const { t } = useTranslation();
    return (React.createElement(Popup, { onClose: onClose },
        React.createElement(PopupHeading, { subHeading: "" }, t("An invite was sent to the following recipients")),
        React.createElement(PopupContent, null, recipients.map((recipient, index) => (React.createElement("p", { key: index }, recipient)))),
        React.createElement(PopupActions, null,
            React.createElement(Button, { variant: "contained", color: "primary", onClick: onInviteMore },
                React.createElement(Trans, null, "Send more invites")),
            React.createElement(Button, { variant: "contained", color: "primary", onClick: onClose },
                React.createElement(Trans, null, "Done")))));
};
const SelectRecipients = ({ onClose, selectedPictureSets, onSelectRecipientsComplete, recipients, onAddRecipient, onUpdateRecipientByIndex, isSubmitting, onGoBack, }) => {
    const [t] = useTranslation();
    const classes = useStyles();
    return (React.createElement(Popup, { onClose: onClose, onGoBack: onGoBack, className: classes.popupContainer },
        React.createElement(PopupHeading, { subHeading: "" }, t("Share %1d picture set(s) with friends and family", {
            postProcess: "sprintf",
            sprintf: [selectedPictureSets.length],
        })),
        React.createElement(PopupContent, null,
            React.createElement(Box, { display: "flex", flexDirection: "column", alignItems: "center" },
                React.createElement(Box, { mb: 4 },
                    React.createElement(Typography, { variant: "body1", gutterBottom: true },
                        React.createElement(Trans, null, "Please enter the recipients' email addresses below"))),
                React.createElement(Box, { maxWidth: "250px" },
                    React.createElement("ul", { className: classes.recipientList }, recipients.map((recipient, index) => (React.createElement("li", { key: index },
                        React.createElement(TextField, { id: "recipient_" + index, type: "email", label: t("Email address"), variant: "outlined", autoFocus: true, value: recipient, onChange: onUpdateRecipientByIndex(index) }))))),
                    React.createElement(AddMoreButton, { onClick: onAddRecipient, text: t("Add recipient") }))),
            React.createElement(Box, { mt: 4, display: "flex", justifyContent: "flex-end" },
                React.createElement(LoadingButton, { isLoading: isSubmitting, onClick: onSelectRecipientsComplete, disabled: getValidRecipients(recipients, t).length === 0 }, getValidRecipients(recipients, t).length === 0 ? (React.createElement(Trans, null, "Please add recipients")) : (React.createElement(Trans, null,
                    "Send invite to",
                    " ",
                    {
                        numEmailRecipients: getValidRecipients(recipients, t).length,
                    },
                    " ",
                    "recipients")))))));
};
const NoPictureSetsAvailable = ({ onClose }) => {
    const classes = useStyles();
    return (React.createElement(Popup, { onClose: onClose, className: classes.popupContainer },
        React.createElement(PopupHeading, { subHeading: "" },
            React.createElement(Trans, null, "Share pictures with friends and family?")),
        React.createElement(PopupContent, null,
            React.createElement(Box, { mb: 4 },
                React.createElement(Typography, { variant: "body1" },
                    React.createElement(Trans, null, "You currently don't have any picture sets available for sharing"))),
            React.createElement(Box, { mt: 2, display: "flex", justifyContent: "flex-end" },
                React.createElement(Button, { variant: "contained", color: "primary", onClick: onClose },
                    React.createElement(Trans, null, "Ok"))))));
};
const SelectPictureSets = ({ pictureSets, onClose, selectedPictureSets, handleToggle, onSelectSubjectsComplete, }) => {
    const classes = useStyles();
    const pictureSetsByYear = useMemo(() => {
        var _a;
        if (!(pictureSets === null || pictureSets === void 0 ? void 0 : pictureSets.length))
            return [];
        const byYear = new Map();
        for (const ps of pictureSets) {
            const y = new Date(ps.job.creationTime * 1000).getFullYear();
            const arr = (_a = byYear.get(y)) !== null && _a !== void 0 ? _a : [];
            arr.push(ps);
            byYear.set(y, arr);
        }
        return Array.from(byYear, ([year, pictureSets]) => ({
            year,
            pictureSets,
        }));
    }, [pictureSets]);
    return (React.createElement(Popup, { onClose: onClose, className: classes.popupContainer },
        React.createElement(PopupHeading, { subHeading: "" },
            React.createElement(Trans, null, "Share pictures with friends and family?")),
        React.createElement(PopupContent, null,
            React.createElement(Box, { mb: 4 },
                React.createElement(Typography, { variant: "body1" },
                    React.createElement(Trans, null, "We will send an invitation to the recipients' email address. Please select the picture set(s) you would like to share."))),
            React.createElement("div", { className: classes.subjectList }, pictureSetsByYear.map((pictureSetByYear) => (React.createElement(React.Fragment, { key: pictureSetByYear.year },
                React.createElement(SubjectYearListHeader, { header: pictureSetByYear.year }),
                React.createElement(List, null, pictureSetByYear.pictureSets.map((pictureSet) => {
                    const labelId = `checkbox-list-label-${pictureSet.subject.uuid}`;
                    return (React.createElement(ListItem, { key: pictureSet.subject.uuid, dense: true, disablePadding: true },
                        React.createElement(ListItemButton, { onClick: () => handleToggle(pictureSet.subject.uuid) },
                            React.createElement(ListItemIcon, null,
                                React.createElement(Checkbox, { edge: "start", checked: selectedPictureSets.indexOf(pictureSet.subject.uuid) !== -1, tabIndex: -1, disableRipple: true, inputProps: { "aria-labelledby": labelId } })),
                            React.createElement(ListItemText, { primary: pictureSet.subject.name, secondary: pictureSet.subject.group }))));
                })))))),
            React.createElement(Box, { mt: 2, display: "flex", justifyContent: "flex-end" },
                React.createElement(Button, { variant: "contained", color: "primary", disabled: selectedPictureSets.length === 0, onClick: onSelectSubjectsComplete }, selectedPictureSets.length === 0 ? (React.createElement(Trans, null, "Select picture set(s)")) : (React.createElement(Trans, null,
                    "Share ",
                    { numPictureSets: selectedPictureSets.length },
                    " picture sets")))))));
};
const getValidRecipients = (recipients, t) => {
    const validators = getYUPValidators(t);
    return recipients.filter((recipient) => {
        if (!recipient) {
            return false;
        }
        try {
            validators.EMAIL_ADDRESS.validateSync(recipient);
            return true;
        }
        catch (error) {
            return false;
        }
    });
};
function arrayToQueryString(array, variableName) {
    return array
        .map((element, index) => `${encodeURIComponent(variableName)}[]=${encodeURIComponent(element)}`)
        .join("&");
}
export default SharePictureSets;
